import React from "react";
import {
  trustedByLogosTokens,
  type TrustedByLogosMaxWidthToken,
} from "./TrustedByLogos.tokens";

export type TrustedByLogoImageSource =
  | string
  | {
      src: string;
      width?: number;
      height?: number;
      [key: string]: unknown;
    };

export type TrustedByLogoImage = {
  id?: string | number;
  src?: TrustedByLogoImageSource;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  loading?: "eager" | "lazy";
  decoding?: "async" | "auto" | "sync";
};

export type TrustedByLogosProps = {
  images: TrustedByLogoImage[];
  trustedByText?: string;
  backgroundColor?: string;
  backgroundColour?: string;
  maxWidth?: TrustedByLogosMaxWidthToken | number | string;
  logoMinWidth?: number | string;
  logoMaxWidth?: number | string;
  logoMaxHeight?: number | string;
  logoFilter?: string;
  className?: string;
  ariaLabel?: string;
};

type TrustedByLogosCssVars = React.CSSProperties & {
  "--trusted-by-logos-bg"?: string;
  "--trusted-by-logos-max-width"?: string;
  "--trusted-by-logos-logo-min-width"?: string;
  "--trusted-by-logos-logo-max-width"?: string;
  "--trusted-by-logos-logo-max-height"?: string;
  "--trusted-by-logos-logo-filter"?: string;
};

const t = trustedByLogosTokens;

const trustedByLogosCss = `
.trusted-by-logos {
  --trusted-by-logos-bg: ${t.colors.background};
  --trusted-by-logos-max-width: ${t.maxWidth.xl};
  --trusted-by-logos-logo-min-width: ${t.sizes.logoMinWidth}px;
  --trusted-by-logos-logo-max-width: ${t.sizes.logoMaxWidth}px;
  --trusted-by-logos-logo-max-height: ${t.sizes.logoMaxHeight}px;
  --trusted-by-logos-logo-filter: ${t.image.filter};
  width: 100%;
  overflow: hidden;
  background: var(--trusted-by-logos-bg);
  color: ${t.colors.text};
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.trusted-by-logos,
.trusted-by-logos * {
  box-sizing: border-box;
}
.trusted-by-logos-shell {
  width: 100%;
  max-width: var(--trusted-by-logos-max-width);
  margin: 0 auto;
  padding: 0 ${t.spacing.shellX}px;
}
.trusted-by-logos-rail {
  width: 100%;
  border-top: 1px solid ${t.colors.divider};
  border-bottom: 1px solid ${t.colors.divider};
  padding: ${t.spacing.railY}px 0;
}
.trusted-by-logos-layout {
  display: flex;
  align-items: center;
  gap: ${t.spacing.labelGap}px;
  width: 100%;
  min-width: 0;
}
.trusted-by-logos-label {
  flex: 0 0 ${t.sizes.labelWidth}px;
  margin: 0;
  color: currentColor;
  font-size: ${t.typography.label.fontSize}px;
  line-height: ${t.typography.label.lineHeight};
  font-weight: ${t.typography.label.fontWeight};
  letter-spacing: ${t.typography.label.letterSpacing};
  text-transform: ${t.typography.label.textTransform};
}
.trusted-by-logos-list {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${t.spacing.logoRowGap}px ${t.spacing.logoColumnGap}px;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}
.trusted-by-logos-item {
  display: flex;
  flex: 1 1 var(--trusted-by-logos-logo-min-width);
  align-items: center;
  justify-content: center;
  min-width: 0;
}
.trusted-by-logos-image {
  display: block;
  width: auto;
  max-width: min(100%, var(--trusted-by-logos-logo-max-width));
  max-height: var(--trusted-by-logos-logo-max-height);
  object-fit: contain;
  filter: var(--trusted-by-logos-logo-filter);
  opacity: ${t.image.opacity};
}
@media (max-width: 760px) {
  .trusted-by-logos-rail {
    padding: ${t.spacing.mobileRailY}px 0;
  }
  .trusted-by-logos-layout {
    align-items: flex-start;
    flex-direction: column;
    gap: 24px;
  }
  .trusted-by-logos-label {
    flex-basis: auto;
    width: 100%;
    font-size: 18px;
    line-height: 1.25;
  }
  .trusted-by-logos-list {
    width: 100%;
    justify-content: flex-start;
    gap: ${t.spacing.mobileLogoRowGap}px ${t.spacing.mobileLogoColumnGap}px;
  }
  .trusted-by-logos-item {
    flex: 1 1 calc(50% - (${t.spacing.mobileLogoColumnGap}px / 2));
    max-width: calc(50% - (${t.spacing.mobileLogoColumnGap}px / 2));
    justify-content: flex-start;
  }
  .trusted-by-logos-image {
    max-height: ${t.sizes.mobileLogoMaxHeight}px;
  }
}
@media (max-width: 360px) {
  .trusted-by-logos-item {
    flex-basis: 100%;
    max-width: 100%;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function resolveMaxWidth(value: TrustedByLogosProps["maxWidth"]): string {
  if (!value) return t.maxWidth.xl;
  if (typeof value === "number") return getCssSize(value);
  if (value in t.maxWidth) return t.maxWidth[value as TrustedByLogosMaxWidthToken];
  return value;
}

function getImageSource(image: TrustedByLogoImage): string {
  if (typeof image.src === "string") return image.src;
  if (image.src?.src) return image.src.src;
  return image.thumbnail || image.url || image.original || "";
}

function getImageAlt(image: TrustedByLogoImage): string {
  return image.alt || image.title || "";
}

function getImageKey(image: TrustedByLogoImage, index: number): string | number {
  return image.id || image.title || image.alt || getImageSource(image) || index;
}

export default function TrustedByLogos({
  images,
  trustedByText = "Trusted by",
  backgroundColor,
  backgroundColour,
  maxWidth = "xl",
  logoMinWidth,
  logoMaxWidth,
  logoMaxHeight,
  logoFilter = t.image.filter,
  className = "",
  ariaLabel,
}: TrustedByLogosProps) {
  const resolvedImages = images.filter((image) => !!getImageSource(image));

  if (!resolvedImages.length) return null;

  const sectionStyle: TrustedByLogosCssVars = {
    "--trusted-by-logos-bg": backgroundColor || backgroundColour || t.colors.background,
    "--trusted-by-logos-max-width": resolveMaxWidth(maxWidth),
    "--trusted-by-logos-logo-filter": logoFilter,
  };

  if (logoMinWidth) {
    sectionStyle["--trusted-by-logos-logo-min-width"] = getCssSize(logoMinWidth);
  }

  if (logoMaxWidth) {
    sectionStyle["--trusted-by-logos-logo-max-width"] = getCssSize(logoMaxWidth);
  }

  if (logoMaxHeight) {
    sectionStyle["--trusted-by-logos-logo-max-height"] = getCssSize(logoMaxHeight);
  }

  return (
    <section
      className={`trusted-by-logos ${className}`.trim()}
      data-component="trusted-by-logos"
      aria-label={ariaLabel || trustedByText || undefined}
      style={sectionStyle}
    >
      <style>{trustedByLogosCss}</style>
      <div className="trusted-by-logos-shell">
        <div className="trusted-by-logos-rail">
          <div className="trusted-by-logos-layout">
            {!!trustedByText && <p className="trusted-by-logos-label">{trustedByText}</p>}
            <ul className="trusted-by-logos-list" aria-label={trustedByText || undefined}>
              {resolvedImages.map((image, index) => {
                const src = getImageSource(image);
                const width = image.width || (typeof image.src === "object" ? image.src.width : undefined);
                const height = image.height || (typeof image.src === "object" ? image.src.height : undefined);

                return (
                  <li className="trusted-by-logos-item" key={getImageKey(image, index)}>
                    <img
                      className="trusted-by-logos-image"
                      src={src}
                      alt={getImageAlt(image)}
                      width={width}
                      height={height}
                      loading={image.loading || "lazy"}
                      decoding={image.decoding || "async"}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
